/**
 * Notifications help — `/content/notification-help` (CSR-109; docs/07-mobile/26 reliability
 * checklist).
 *
 * **Built from the spec, not a design.** No design exists for this screen; the owner ruled on
 * 2026-10-26 that design-less screens are built from the docs with the console's own components
 * and restyled later.
 *
 * A pandit whose phone silences push gets "how to let notifications through" steps for their
 * phone's maker. Makers are grouped (Xiaomi, Redmi and Poco share one set); the fallback group is
 * what every maker no group names gets. Each group holds one ordered list of steps per locale, and
 * both writes are whole replaces — so the editors stage their changes and send only on Save. The
 * server content-lints every string; its refusal is shown as it came back.
 */
import { useState } from 'react';
import { Button, Chip, Note } from '../components/primitives';
import { Input } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useNotificationHelp,
  useReplaceHelpSteps,
  useSetHelpMakers,
  type HelpGroup,
  type HelpLocale,
  type HelpStep,
} from '../lib/api/hooks/notificationHelp';
import { count } from '../lib/text';

/** The server's caps (`notification_help_admin.py`). */
export const MAX_STEPS = 12;
export const MAX_MAKERS = 40;

const LOCALES: readonly { value: HelpLocale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
];

const languageOf = (locale: HelpLocale) => LOCALES.find((l) => l.value === locale)!.label;

/**
 * What saving a list does, said as the server resolves it (`notification_help.resolve`): a phone
 * reads its own group, never the fallback's, so an empty English list shows that group nothing,
 * while an empty Hindi list falls back to the group's English steps.
 */
export function replaceConsequence(group: string, locale: HelpLocale, steps: number): string {
  if (steps > 0) {
    return `Every pandit whose phone is in ${group} sees ${count(steps, 'step')} in ${languageOf(locale)} from their next open; the old list is replaced, not kept.`;
  }
  return locale === 'en'
    ? `Pandits whose phone is in ${group} read the fallback group's steps instead from their next open.`
    : `Pandits whose phone is in ${group} read its English steps instead from their next open, or the fallback group's if it has none.`;
}

const NOTE_STYLE = { color: 'var(--c-text-muted)', fontSize: 'var(--fs-admin-colhead)' } as const;

/** What a step editor holds: every field a string, so an empty image is "" until it is sent. */
type Draft = { title: string; body: string; image_url: string };

const toDraft = (steps: readonly { title: string; body: string; image_url?: string | null }[]): Draft[] =>
  steps.map((s) => ({ title: s.title, body: s.body, image_url: s.image_url ?? '' }));

export function stepsPayload(rows: readonly Draft[]): HelpStep[] {
  return rows.map((r) => ({ title: r.title.trim(), body: r.body.trim(), image_url: r.image_url.trim() || null }));
}

/** The same limits the server enforces, so the operator sees them before a 422. */
export function stepProblems(rows: readonly Draft[]): string[] {
  const out: string[] = [];
  rows.forEach((r, i) => {
    const n = i + 1;
    const title = r.title.trim();
    const body = r.body.trim();
    const image = r.image_url.trim();
    if (!title) out.push(`Step ${n} needs a title.`);
    else if (title.length > 120) out.push(`Step ${n}'s title is over 120 characters.`);
    if (!body) out.push(`Step ${n} needs a body.`);
    else if (body.length > 1000) out.push(`Step ${n}'s body is over 1000 characters.`);
    if (image && !image.startsWith('https://')) out.push(`Step ${n}'s image must be an https:// link.`);
    else if (image.length > 500) out.push(`Step ${n}'s image link is over 500 characters.`);
  });
  return out;
}

/** Makers as the app reports them: lower-case, trimmed, each once, in the order typed. */
export function parseMakers(text: string): string[] {
  const seen = new Set<string>();
  for (const part of text.split(',')) {
    const m = part.trim().toLowerCase();
    if (m) seen.add(m);
  }
  return [...seen];
}

function StepsEditor({ group, locale, source }: { group: string; locale: HelpLocale; source: Draft[] }) {
  const { scope, toast } = useConsole();
  const replace = useReplaceHelpSteps();
  const [confirming, setConfirming] = useState(false);
  const [rows, setRows] = useState<Draft[]>(source);
  const dirty = JSON.stringify(rows) !== JSON.stringify(source);
  const problems = stepProblems(rows);
  const update = (i: number, patch: Partial<Draft>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, by: number) => {
    const next = [...rows];
    next.splice(i + by, 0, ...next.splice(i, 1));
    setRows(next);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
      {rows.length === 0 && <span style={NOTE_STYLE}>No steps in this language yet.</span>}
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'grid', gap: 'var(--sp-xs)', paddingBottom: 'var(--sp-sm)', borderBottom: '1px solid var(--c-border)' }}>
          <Input label={`Step ${i + 1} title`} value={row.title} onChange={(v) => update(i, { title: v })} />
          <Input label={`Step ${i + 1} body`} value={row.body} onChange={(v) => update(i, { body: v })} multiline />
          <Input label={`Step ${i + 1} image link`} value={row.image_url} onChange={(v) => update(i, { image_url: v })} placeholder="https://…" />
          <div style={{ display: 'flex', gap: 'var(--sp-xs)' }}>
            <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)}>
              Move step {i + 1} up
            </Button>
            <Button size="sm" variant="ghost" disabled={i === rows.length - 1} onClick={() => move(i, 1)}>
              Move step {i + 1} down
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove step {i + 1}
            </Button>
          </div>
        </div>
      ))}
      {problems.length > 0 && (
        <ul aria-label="Fix before saving" style={{ margin: 0, color: 'var(--c-warn-text)' }}>
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
        <Button
          size="sm"
          variant="secondary"
          disabled={rows.length >= MAX_STEPS}
          onClick={() => setRows([...rows, { title: '', body: '', image_url: '' }])}
        >
          Add step
        </Button>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!dirty || problems.length > 0}
          loading={replace.isPending}
          onClick={() => setConfirming(true)}
        >
          Save steps
        </ScopeAction>
        {confirming && (
          // A whole replace of what pandits read, so it is typed, not clicked.
          <ConfirmAction
            open
            title={`Replace the ${languageOf(locale)} steps for ${group}?`}
            phrase="SAVE"
            consequences={[
              replaceConsequence(group, locale, rows.length),
              'Each string is content-linted on save; a refusal comes back quoted.',
            ]}
            confirmLabel="Save steps"
            onCancel={() => setConfirming(false)}
            onConfirm={() => {
              replace.mutate(
                { group, locale, steps: stepsPayload(rows) },
                {
                  onSuccess: () => toast({ tone: 'success', message: `Steps saved for ${group}.` }),
                  onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                },
              );
              setConfirming(false);
            }}
          />
        )}
        <span style={NOTE_STYLE}>
          {rows.length} of {MAX_STEPS} steps{dirty ? ' · Unsaved — pandits still see the old steps.' : ''}
        </span>
      </div>
    </div>
  );
}

function MakersEditor({ group, source }: { group: string; source: string[] }) {
  const { scope, toast } = useConsole();
  const save = useSetHelpMakers();
  const [confirming, setConfirming] = useState(false);
  const [text, setText] = useState(source.join(', '));
  const makers = parseMakers(text);
  const dirty = JSON.stringify(makers) !== JSON.stringify(source);
  const over = makers.length > MAX_MAKERS;

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
      <Input
        label={`Makers in ${group}`}
        value={text}
        onChange={setText}
        helper="Comma-separated, as the phone reports its maker — for example xiaomi, redmi, poco."
        error={over ? `At most ${MAX_MAKERS} makers in one group.` : undefined}
      />
      <div>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!dirty || over}
          loading={save.isPending}
          onClick={() => setConfirming(true)}
        >
          Save makers
        </ScopeAction>
        {confirming && (
          // Moving a maker moves every pandit on that phone to another group's steps.
          <ConfirmAction
            open
            title={`Set the makers in ${group}?`}
            phrase="SAVE"
            consequences={[
              `Pandits on ${makers.length === 0 ? 'no maker' : makers.join(', ')} read ${group}'s steps from their next open.`,
              'A maker taken out of every group falls back to the fallback group.',
            ]}
            confirmLabel="Save makers"
            onCancel={() => setConfirming(false)}
            onConfirm={() => {
              save.mutate(
                { group, manufacturers: makers },
                {
                  onSuccess: () => toast({ tone: 'success', message: `Makers saved for ${group}.` }),
                  onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                },
              );
              setConfirming(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

function GroupPanel({ group }: { group: HelpGroup }) {
  const [locale, setLocale] = useState<HelpLocale>('en');
  const source = toDraft(group.steps[locale] ?? []);
  return (
    <Section
      title={group.group_key}
      note={group.is_fallback ? 'Fallback — every maker no other group names' : count(group.manufacturers.length, 'maker')}
    >
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        {group.is_fallback ? (
          <span style={NOTE_STYLE}>This group has no makers of its own: it is what any phone not listed elsewhere sees.</span>
        ) : (
          <MakersEditor key={group.manufacturers.join(',')} group={group.group_key} source={group.manufacturers} />
        )}
        <div role="group" aria-label={`Language, ${group.group_key}`} style={{ display: 'flex', gap: 'var(--sp-xs)' }}>
          {LOCALES.map((l) => (
            <Chip
              key={l.value}
              variant="filter"
              selected={l.value === locale}
              label={`${l.label} (${(group.steps[l.value] ?? []).length})`}
              onClick={() => setLocale(l.value)}
            />
          ))}
        </div>
        <StepsEditor key={`${locale}:${JSON.stringify(source)}`} group={group.group_key} locale={locale} source={source} />
      </div>
    </Section>
  );
}

export function NotificationHelp() {
  const query = useNotificationHelp();
  const groups = query.data?.items ?? [];
  return (
    <>
      <Note>
        The steps a pandit sees when their phone is silencing booking alerts, chosen by the phone&apos;s maker.
        Each language is saved on its own.
      </Note>
      {query.isPending && <LoadingState message="Reading the help steps…" />}
      {query.isError && <ErrorState title="Could not read the help steps" cause={errorMessage(query.error)} />}
      {query.isSuccess && groups.length === 0 && (
        <EmptyState icon="bell" title="No maker groups yet" body="The groups are seeded; an empty list means the seed has not run here." />
      )}
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        {groups.map((g) => (
          <GroupPanel key={g.group_key} group={g} />
        ))}
      </div>
    </>
  );
}
